import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getEqualSplitDistribution } from "../utils/splitCalc.ts";
import type { SplitAmountMode } from "./types";

export const getHydratedSplitAmountState = ({
  splitAmountMode,
  splitAmount,
  remainingAmount,
  splitPeopleCount,
}: {
  splitAmountMode: SplitAmountMode | null | undefined;
  splitAmount: number | null | undefined;
  remainingAmount: number | undefined;
  splitPeopleCount: number | null | undefined;
}): {
  nextSplitAmountMode: SplitAmountMode;
  nextSplitAmount: string;
  nextSplitPeopleCount: string;
} => {
  const nextSplitAmountMode = splitAmountMode === "equal" ? "equal" : "manual";
  const nextSplitAmount =
    nextSplitAmountMode === "manual"
      ? String(Math.max(0, Math.round(Number(splitAmount ?? remainingAmount ?? 0))))
      : "";
  const nextSplitPeopleCount = Math.max(2, Number(splitPeopleCount) || 2);

  return {
    nextSplitAmountMode,
    nextSplitAmount,
    nextSplitPeopleCount: String(nextSplitPeopleCount),
  };
};

export const useSplitAmount = ({
  getRemainingAmount,
}: {
  getRemainingAmount: () => number;
}) => {
  const { t } = useTranslation();
  const [splitAmountMode, setSplitAmountMode] = useState<SplitAmountMode>("manual");
  const [splitAmountInput, setSplitAmountInput] = useState<string>("");
  const [splitPeopleCount, setSplitPeopleCount] = useState<string>("2");

  const isWholeNumberValue = (value: string | number | null | undefined): boolean => {
    if (value === "" || value == null) return false;
    const normalized = String(value).trim();
    return /^\d+$/.test(normalized);
  };

  const { amountSplitPreview, splitAmountWarning } = useMemo(() => {
    const remainingAmount = getRemainingAmount();
    if (!remainingAmount) {
      return { amountSplitPreview: 0, splitAmountWarning: "" };
    }

    if (splitAmountMode === "equal") {
      const peopleCountNum = Number(splitPeopleCount);
      const isPeopleCountValid = Number.isInteger(peopleCountNum) && peopleCountNum >= 1;
      const isRemainingAmountValid = Number.isInteger(remainingAmount) && remainingAmount >= 0;

      if (!isPeopleCountValid || !isRemainingAmountValid) {
        return { amountSplitPreview: 0, splitAmountWarning: "" };
      }

      return {
        amountSplitPreview: getEqualSplitDistribution(remainingAmount, peopleCountNum).currentShare,
        splitAmountWarning: "",
      };
    }

    if (!isWholeNumberValue(splitAmountInput)) {
      return {
        amountSplitPreview: 0,
        splitAmountWarning: splitAmountInput ? t("label_split_amount_integer_error") : "",
      };
    }

    if (Number(splitAmountInput) > remainingAmount) {
      return {
        amountSplitPreview: 0,
        splitAmountWarning: t("label_split_amount_exceeds_remaining"),
      };
    }

    return {
      amountSplitPreview: Number(splitAmountInput),
      splitAmountWarning: "",
    };
  }, [getRemainingAmount, splitAmountInput, splitAmountMode, splitPeopleCount, t]);

  return {
    splitAmountMode,
    setSplitAmountMode,
    splitAmountInput,
    setSplitAmountInput,
    splitPeopleCount,
    setSplitPeopleCount,
    amountSplitPreview,
    splitAmountWarning,
    isWholeNumberValue,
  };
};
